import React, { useCallback, useRef, createRef, useEffect, useState } from 'react';
import { useGlobalEnvConfig } from "@environment/GlobalEnvConfigProvider";
import { Map, Marker, Source, Layer, NavigationControl } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import Timeline from './timeline';
import OverLappingPins from './overlapping-pins';

import 'mapbox-gl/dist/mapbox-gl.css';
import './style.scss';

// eslint-disable-next-line import/no-webpack-loader-syntax, import/no-unresolved
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const InteractiveMap = ({ activeMarkers, onMapLoad, markerData, layerData, config, timelineConfig, onMarkerClick, onZoom, onRecenter, onTimelineOpen }) => {
    const { REACT_APP_MAPBOX_ACCESS_TOKEN, REACT_APP_MAPBOX_STYLE } = useGlobalEnvConfig();
    const defaultMapStyles = { position: 'relative', width: '100%', height: '85vh', zIndex: 0 }
    const map = useRef();
    const timelineRefs = useRef([]);
    const mapRefs = useRef([]);
    const overlappingPinsRefs = useRef([]);

    const [overlappingPins, setOverlappingPins] = useState([]);
    const [mapConfig, setMapConfig] = useState(null);
    const prevIndex = useRef();

    const handleMarkerClick = useCallback((i, name) => {
        if (Number.isInteger(i)) {
            const marker = mapRefs?.current[i].current;
            onMarkerClick(marker, i, prevIndex.current, timelineRefs, map, name);
        }
    }, [onMarkerClick]);

    const handleRecenterClick = useCallback((i) => {
        onRecenter();
    }, [onRecenter]);

    const zoomHandler = useCallback((e) => {
        if (e) {
            onZoom(e)
        }
    }, [onZoom]);

    const mapLoad = useCallback(() => {
        if (map && timelineRefs && mapRefs && overlappingPinsRefs && overlappingPins) {
            onMapLoad(map, timelineRefs, mapRefs, overlappingPinsRefs, overlappingPins)
        }
    }, [onMapLoad, overlappingPins]);


    useEffect(() => {
        if (markerData) {
            mapRefs.current = markerData?.map((element, i) => (
                mapRefs.current[i] ?? createRef()
            ))

            timelineRefs.current = markerData?.map((element, i) => (
                timelineRefs.current[i] ?? createRef()
            ))

            //Group markers with the same lat long values - It will always find itself so need to check for more than 1
            const grouped = Object.values(markerData.filter(r => r.type !== "route").reduce((acc, cur) => {
                acc[cur.latitude && cur.longitude] = [...(acc[cur.latitude && cur.longitude] || []), cur];
                return acc;
            }, {})).filter(x => x.length > 1);

            setOverlappingPins(grouped)
        }
    }, [markerData]);

    useEffect(() => {
        if (config) {
            setMapConfig(config);
        }

        map?.current?.resize()
    }, [config]);

    useEffect(() => {
        if (overlappingPins.length) {
            overlappingPinsRefs.current = overlappingPins?.map((element, i) => (
                overlappingPinsRefs.current[i] ?? createRef()
            ))
        }
    }, [overlappingPins]);

    return (
        <>

            <div style={{ ...config?.mapWrapperStyles }} className={`interactive-map ${timelineConfig ? timelineConfig.timeLineOptions?.alignment : "left"}`}>
                {mapConfig &&
                    <Map
                        ref={map}
                        onLoad={() => mapLoad()}
                        reuseMaps
                        optimizeForTerrain
                        onZoom={(e) => zoomHandler(e)}
                        maxZoom={config?.maxZoom ? config?.maxZoom : 14}
                        cooperativeGestures
                        interactive
                        pitchWithRotate={false}
                        style={config?.mapStyles ? config.mapStyles : defaultMapStyles}
                        padding={config?.mapPadding ? config?.mapPadding : 0}
                        mapStyle={`mapbox://styles/${REACT_APP_MAPBOX_STYLE}?optimize=true`}
                        mapboxAccessToken={REACT_APP_MAPBOX_ACCESS_TOKEN}
                        projection={'mercator'}
                        initialViewState={config?.initialViewState ? config?.initialViewState : { zoom: 0 }}
                        logoPosition={"bottom-right"}
                        localFontFamily={config?.mapFont ? config?.mapFont : ''}
                    >
                        {layerData && layerData.filter(x => x.geoJson).map((routeJson, i) => (
                            <React.Fragment key={i}>
                                <Source id={`route-source-${i}`} type="geojson" data={routeJson.geoJson}>
                                    {routeJson.layerStyles.map((style, index) => (
                                        <Layer key={index} {...style} />
                                    ))}
                                </Source>
                            </React.Fragment>
                        ))}

                        {markerData && markerData.map((marker, i) => (
                            <React.Fragment key={i}>
                                {!overlappingPins.filter((group) => group.some((p) => marker.markerIndex === p.markerIndex)).length && marker?.children?.mapChildren &&
                                    <Marker onClick={() => handleMarkerClick(i)} longitude={marker.longitude} latitude={marker.latitude} style={{ zIndex: activeMarkers?.includes(i) ? 2 : 0 }} >
                                        <div className='marker-content' ref={mapRefs?.current[i]}>
                                            {marker?.children?.mapChildren}
                                        </div>
                                    </Marker>
                                }
                            </React.Fragment>
                        ))}

                        {overlappingPins?.length > 0 && overlappingPins?.map((pins, i) => (
                            <React.Fragment key={i}>
                                <OverLappingPins pins={pins} index={i} mapRefs={mapRefs} overlappingPinsRefs={overlappingPinsRefs} config={config} handleMarkerClick={handleMarkerClick} />
                            </React.Fragment>
                        ))}

                        {config?.showControls &&
                            <NavigationControl />
                        }
                    </Map>
                }

                {markerData && timelineRefs &&
                    <Timeline markerData={markerData} timelineRefs={timelineRefs} config={timelineConfig} handleMarkerClick={handleMarkerClick} handleRecenterClick={handleRecenterClick} onTimelineOpen={onTimelineOpen} />
                }
            </div>

        </>
    )
}

export default InteractiveMap;